import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Plus, Download, CreditCard, Trash2, Pencil, Printer } from 'lucide-react';
import { exportCSV, exportXLSX, exportPDF, printData } from '../utils/exportUtils';

const RECEIVABLE_CATEGORIES = ['Pharmacy', 'Pathology', 'Radiology', 'ABHA', 'NABH', 'Refer', 'Insurance'];

const AccountsPage = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('purchases');
  const [purchases, setPurchases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);

  const [receivables, setReceivables] = useState([]);
  const [receivableCategories, setReceivableCategories] = useState([]);
  const [receivableParties, setReceivableParties] = useState([]);
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [editingReceivable, setEditingReceivable] = useState(null);
  const [submittingCredit, setSubmittingCredit] = useState(false);

  const [partyName, setPartyName] = useState('');
  const [partyContact, setPartyContact] = useState('');
  const [creditDate, setCreditDate] = useState(new Date().toISOString().split('T')[0]);
  const [creditCategory, setCreditCategory] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [creditAmount, setCreditAmount] = useState('');

  const [receiptModal, setReceiptModal] = useState(null);
  const [receiptAmount, setReceiptAmount] = useState('');
  const [receiptMethod, setReceiptMethod] = useState('cash');
  const [receiptRef, setReceiptRef] = useState('');
  const [submittingReceipt, setSubmittingReceipt] = useState(false);

  const [partyFilter, setPartyFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [rMonthFilter, setRMonthFilter] = useState('');
  const [rFromDate, setRFromDate] = useState('');
  const [rToDate, setRToDate] = useState('');

  const [vendorName, setVendorName] = useState('');
  const [vendorContact, setVendorContact] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [particulars, setParticulars] = useState([{ description: '', category: '', subCategory: '', quantity: 1, rate: 0, amount: 0 }]);

  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [submittingPurchase, setSubmittingPurchase] = useState(false);

  const [vendorFilter, setVendorFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [purchaseDateSort, setPurchaseDateSort] = useState('desc');
  const [receivableDateSort, setReceivableDateSort] = useState('desc');

  const { vendorNames, vendorContacts } = useMemo(() => {
    const contacts = {};
    const sorted = [...purchases].sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
    const names = [];
    sorted.forEach(p => {
      if (p.vendorName && !(p.vendorName in contacts)) {
        contacts[p.vendorName] = p.vendorContact || '';
        names.push(p.vendorName);
      }
    });
    return { vendorNames: names, vendorContacts: contacts };
  }, [purchases]);

  const handleVendorNameChange = (e) => {
    const value = e.target.value;
    setVendorName(value);
    if (value === '') {
      setVendorContact('');
    } else if (vendorContacts[value] !== undefined) {
      setVendorContact(vendorContacts[value]);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [purchasesRes, categoriesRes, subCategoriesRes, receivablesRes, rCategoriesRes, rPartiesRes] = await Promise.all([
        api.get('/purchases'),
        api.get('/purchases/categories'),
        api.get('/purchases/subcategories'),
        api.get('/receivables'),
        api.get('/receivables/categories'),
        api.get('/receivables/parties')
      ]);
      setPurchases(purchasesRes.data);
      setCategories((categoriesRes.data || []).filter(Boolean));
      setSubCategories((subCategoriesRes.data || []).filter(Boolean));
      setReceivables(receivablesRes.data);
      setReceivableCategories((rCategoriesRes.data || []).filter(Boolean));
      setReceivableParties((rPartiesRes.data || []).filter(Boolean));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load accounts data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredPurchases = useMemo(() => {
    const filtered = purchases.filter(p => {
      const purchaseDate = new Date(p.purchaseDate);
      const matchesVendor = !vendorFilter || p.vendorName?.toLowerCase().includes(vendorFilter.toLowerCase());
      if (!matchesVendor) return false;

      if (monthFilter) {
        const [year, month] = monthFilter.split('-');
        if (purchaseDate.getFullYear() !== parseInt(year) || purchaseDate.getMonth() + 1 !== parseInt(month)) {
          return false;
        }
      }

      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        if (purchaseDate < from) return false;
      }

      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        if (purchaseDate > to) return false;
      }

      return true;
    });
    return [...filtered].sort((a, b) => {
      const aDate = new Date(a.purchaseDate);
      const bDate = new Date(b.purchaseDate);
      return purchaseDateSort === 'asc' ? aDate - bDate : bDate - aDate;
    });
  }, [purchases, vendorFilter, fromDate, toDate, monthFilter, purchaseDateSort]);

  const analysis = useMemo(() => {
    const total = filteredPurchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
    const paid = filteredPurchases.reduce((sum, p) => sum + (p.totalPaid || 0), 0);
    const unpaid = filteredPurchases.reduce((sum, p) => sum + (p.balanceAmount || 0), 0);
    return { count: filteredPurchases.length, total, paid, unpaid };
  }, [filteredPurchases]);

  const { partyContacts } = useMemo(() => {
    const contacts = {};
    const sorted = [...receivables].sort((a, b) => new Date(b.creditDate) - new Date(a.creditDate));
    sorted.forEach(r => {
      if (r.partyName && !(r.partyName in contacts)) {
        contacts[r.partyName] = r.partyContact || '';
      }
    });
    return { partyContacts: contacts };
  }, [receivables]);

  const filteredReceivables = useMemo(() => {
    const filtered = receivables.filter(r => {
      const creditDate = new Date(r.creditDate);
      if (partyFilter && !r.partyName?.toLowerCase().includes(partyFilter.toLowerCase())) return false;
      if (categoryFilter && r.category !== categoryFilter) return false;

      if (rMonthFilter) {
        const [year, month] = rMonthFilter.split('-');
        if (creditDate.getFullYear() !== parseInt(year) || creditDate.getMonth() + 1 !== parseInt(month)) {
          return false;
        }
      }

      if (rFromDate) {
        const from = new Date(rFromDate);
        from.setHours(0, 0, 0, 0);
        if (creditDate < from) return false;
      }

      if (rToDate) {
        const to = new Date(rToDate);
        to.setHours(23, 59, 59, 999);
        if (creditDate > to) return false;
      }

      return true;
    });
    return [...filtered].sort((a, b) => {
      const aDate = new Date(a.creditDate);
      const bDate = new Date(b.creditDate);
      return receivableDateSort === 'asc' ? aDate - bDate : bDate - aDate;
    });
  }, [receivables, partyFilter, categoryFilter, rMonthFilter, rFromDate, rToDate, receivableDateSort]);

  const receivableAnalysis = useMemo(() => {
    const total = filteredReceivables.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
    const received = filteredReceivables.reduce((sum, r) => sum + (r.totalReceived || 0), 0);
    const pending = filteredReceivables.reduce((sum, r) => sum + (r.balanceAmount || 0), 0);
    return { count: filteredReceivables.length, total, received, pending };
  }, [filteredReceivables]);

  const purchaseReportSubtitle = useMemo(() => {
    const filters = [];
    if (vendorFilter) filters.push(`Vendor: ${vendorFilter}`);
    if (monthFilter) filters.push(`Month: ${monthFilter}`);
    if (fromDate) filters.push(`From: ${new Date(fromDate).toLocaleDateString('en-GB')}`);
    if (toDate) filters.push(`To: ${new Date(toDate).toLocaleDateString('en-GB')}`);
    filters.push(`Order: ${purchaseDateSort === 'asc' ? 'Oldest First' : 'Newest First'}`);
    const filterLine = filters.join(' | ');
    const summaryLine = `Bills: ${analysis.count} | Total: ₹${analysis.total.toFixed(2)} | Paid: ₹${analysis.paid.toFixed(2)} | Unpaid: ₹${analysis.unpaid.toFixed(2)}`;
    return `${filterLine}\n${summaryLine}`;
  }, [vendorFilter, monthFilter, fromDate, toDate, purchaseDateSort, analysis]);

  const receivableReportSubtitle = useMemo(() => {
    const filters = [];
    if (partyFilter) filters.push(`Party: ${partyFilter}`);
    if (categoryFilter) filters.push(`Category: ${categoryFilter}`);
    if (rMonthFilter) filters.push(`Month: ${rMonthFilter}`);
    if (rFromDate) filters.push(`From: ${new Date(rFromDate).toLocaleDateString('en-GB')}`);
    if (rToDate) filters.push(`To: ${new Date(rToDate).toLocaleDateString('en-GB')}`);
    filters.push(`Order: ${receivableDateSort === 'asc' ? 'Oldest First' : 'Newest First'}`);
    const filterLine = filters.join(' | ');
    const summaryLine = `Credits: ${receivableAnalysis.count} | Total: ₹${receivableAnalysis.total.toFixed(2)} | Received: ₹${receivableAnalysis.received.toFixed(2)} | To Collect: ₹${receivableAnalysis.pending.toFixed(2)}`;
    return `${filterLine}\n${summaryLine}`;
  }, [partyFilter, categoryFilter, rMonthFilter, rFromDate, rToDate, receivableDateSort, receivableAnalysis]);

  const allCreditCategories = useMemo(() => {
    const merged = [...RECEIVABLE_CATEGORIES];
    receivableCategories.forEach(c => {
      if (c && !merged.includes(c)) merged.push(c);
    });
    return merged;
  }, [receivableCategories]);

  const resetFilters = () => {
    setVendorFilter('');
    setFromDate('');
    setToDate('');
    setMonthFilter('');
    setPurchaseDateSort('desc');
  };

  const resetReceivableFilters = () => {
    setPartyFilter('');
    setCategoryFilter('');
    setRMonthFilter('');
    setRFromDate('');
    setRToDate('');
    setReceivableDateSort('desc');
  };

  const resetCreditForm = () => {
    setEditingReceivable(null);
    setPartyName('');
    setPartyContact('');
    setCreditDate(new Date().toISOString().split('T')[0]);
    setCreditCategory('');
    setCreditDescription('');
    setCreditAmount('');
  };

  const handlePartyNameChange = (e) => {
    const value = e.target.value;
    setPartyName(value);
    if (value === '') {
      setPartyContact('');
    } else if (partyContacts[value] !== undefined) {
      setPartyContact(partyContacts[value]);
    }
  };

  const handleCreditSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const amount = parseFloat(creditAmount);
      if (!amount || amount <= 0) {
        setError('Enter a valid credit amount');
        return;
      }
      if (!creditCategory) {
        setError('Category is required');
        return;
      }
      setSubmittingCredit(true);
      const payload = {
        partyName,
        partyContact,
        category: creditCategory,
        description: creditDescription,
        creditDate,
        totalAmount: amount
      };
      if (editingReceivable) {
        await api.put(`/receivables/${editingReceivable._id}`, payload);
      } else {
        await api.post('/receivables', payload);
      }
      resetCreditForm();
      setShowCreditForm(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.map(e2 => e2.msg).join(', ') || `Failed to ${editingReceivable ? 'update' : 'create'} credit`);
    } finally {
      setSubmittingCredit(false);
    }
  };

  const openReceipt = (receivable) => {
    setReceiptModal(receivable);
    setReceiptAmount(receivable.balanceAmount);
    setReceiptMethod('cash');
    setReceiptRef('');
  };

  const submitReceipt = async () => {
    try {
      setError('');
      const amount = parseFloat(receiptAmount);
      if (!amount || amount <= 0) {
        alert('Enter a valid receipt amount');
        return;
      }
      if (receiptMethod !== 'cash' && !receiptRef) {
        alert('Reference/UTR is required for non-cash receipts');
        return;
      }
      setSubmittingReceipt(true);
      await api.post(`/receivables/${receiptModal._id}/receipt`, {
        amount,
        paymentMethod: receiptMethod,
        referenceNumber: receiptRef
      });
      setReceiptModal(null);
      setReceiptAmount('');
      setReceiptMethod('cash');
      setReceiptRef('');
      fetchData();
    } catch (err) {
      if (err.response?.status === 409) {
        setReceiptModal(null);
        setReceiptAmount('');
        setReceiptMethod('cash');
        setReceiptRef('');
        fetchData();
      } else {
        setError(err.response?.data?.message || 'Failed to record receipt');
      }
    } finally {
      setSubmittingReceipt(false);
    }
  };

  const handleEditReceivable = (receivable) => {
    setEditingReceivable(receivable);
    setPartyName(receivable.partyName || '');
    setPartyContact(receivable.partyContact || '');
    setCreditDate(new Date(receivable.creditDate).toISOString().split('T')[0]);
    setCreditCategory(receivable.category || '');
    setCreditDescription(receivable.description || '');
    setCreditAmount(receivable.totalAmount);
    setShowCreditForm(true);
  };

  const handleDeleteReceivable = async (receivable) => {
    if (!window.confirm(`Are you sure you want to delete credit ${receivable.creditNumber}? This action cannot be undone.`)) {
      return;
    }
    try {
      setError('');
      await api.delete(`/receivables/${receivable._id}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete credit');
    }
  };

  const getReceivableStatusClass = (status) => {
    switch (status) {
      case 'received': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleParticularChange = (idx, field, value) => {
    setParticulars(prev => prev.map((p, i) => {
      if (i !== idx) return p;
      const updated = { ...p, [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : p.quantity;
        const rate = field === 'rate' ? parseFloat(value) || 0 : p.rate;
        updated.amount = qty * rate;
      }
      return updated;
    }));
  };

  const addParticular = () => {
    setParticulars([...particulars, { description: '', category: '', subCategory: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeParticular = (idx) => {
    setParticulars(particulars.filter((_, i) => i !== idx));
  };

  const totalAmount = particulars.reduce((sum, p) => sum + p.amount, 0);

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSubmittingPurchase(true);
      const payload = {
        vendorName,
        vendorContact,
        purchaseDate,
        particulars
      };
      if (editingPurchase) {
        await api.put(`/purchases/${editingPurchase._id}`, payload);
      } else {
        await api.post('/purchases', payload);
      }
      setVendorName('');
      setVendorContact('');
      setPurchaseDate(new Date().toISOString().split('T')[0]);
      setParticulars([{ description: '', category: '', subCategory: '', quantity: 1, rate: 0, amount: 0 }]);
      setEditingPurchase(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.map(e => e.msg).join(', ') || `Failed to ${editingPurchase ? 'update' : 'create'} purchase`);
    } finally {
      setSubmittingPurchase(false);
    }
  };

  const openPayment = (purchase) => {
    setPaymentModal(purchase);
    setPaymentAmount(purchase.balanceAmount);
    setPaymentMethod('cash');
    setReferenceNumber('');
  };

  const submitPayment = async () => {
    try {
      setError('');
      const amount = parseFloat(paymentAmount);
      if (!amount || amount <= 0) {
        alert('Enter a valid payment amount');
        return;
      }
      if (paymentMethod !== 'cash' && !referenceNumber) {
        alert('Reference/UTR is required for non-cash payments');
        return;
      }
      setSubmittingPayment(true);
      await api.post(`/purchases/${paymentModal._id}/payment`, {
        amount,
        paymentMethod,
        referenceNumber
      });
      setPaymentModal(null);
      setPaymentAmount('');
      setPaymentMethod('cash');
      setReferenceNumber('');
      fetchData();
    } catch (err) {
      // 409 = duplicate payment already recorded; treat as success
      if (err.response?.status === 409) {
        setPaymentModal(null);
        setPaymentAmount('');
        setPaymentMethod('cash');
        setReferenceNumber('');
        fetchData();
      } else {
        setError(err.response?.data?.message || 'Failed to record payment');
      }
    } finally {
      setSubmittingPayment(false);
    }
  };

  const downloadVoucher = async (id, billNumber) => {
    try {
      setError('');
      const response = await api.get(`/purchases/${id}/voucher`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voucher-${billNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download voucher');
    }
  };

  const canManagePurchases = user?.role === 'hospital_admin' || user?.role === 'super_admin';

  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    setVendorName(purchase.vendorName || '');
    setVendorContact(purchase.vendorContact || '');
    setPurchaseDate(new Date(purchase.purchaseDate).toISOString().split('T')[0]);
    setParticulars(purchase.particulars.map(p => ({
      description: p.description,
      category: p.category,
      subCategory: p.subCategory || '',
      quantity: p.quantity,
      rate: p.rate,
      amount: p.amount
    })));
    setShowForm(true);
  };

  const handleDeletePurchase = async (purchase) => {
    if (!window.confirm(`Are you sure you want to delete purchase ${purchase.billNumber}? This action cannot be undone.`)) {
      return;
    }
    try {
      setError('');
      await api.delete(`/purchases/${purchase._id}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete purchase');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const purchaseExportColumns = [
    { header: 'Bill No', key: 'billNumber' },
    { header: 'Vendor', key: 'vendorName' },
    { header: 'Contact', key: 'vendorContact' },
    { header: 'Date', accessor: (p) => new Date(p.purchaseDate).toLocaleDateString('en-GB') },
    { header: 'Total', accessor: (p) => `₹${p.totalAmount.toFixed(2)}`, align: 'right' },
    { header: 'Paid', accessor: (p) => `₹${p.totalPaid.toFixed(2)}`, align: 'right' },
    { header: 'Status', accessor: (p) => p.status?.toUpperCase() || '' }
  ];

  const receivableExportColumns = [
    { header: 'Credit No', key: 'creditNumber' },
    { header: 'Party', key: 'partyName' },
    { header: 'Contact', key: 'partyContact' },
    { header: 'Category', key: 'category' },
    { header: 'Description', key: 'description' },
    { header: 'Date', accessor: (r) => new Date(r.creditDate).toLocaleDateString('en-GB') },
    { header: 'Total', accessor: (r) => `₹${r.totalAmount.toFixed(2)}`, align: 'right' },
    { header: 'Received', accessor: (r) => `₹${r.totalReceived.toFixed(2)}`, align: 'right' },
    { header: 'Status', accessor: (r) => r.status?.toUpperCase() || '' }
  ];

  const handleExport = (format) => {
    if (activeTab === 'receivables') {
      const filename = `receivables-${new Date().toISOString().split('T')[0]}`;
      const data = filteredReceivables;
      if (format === 'csv') exportCSV(data, filename, receivableExportColumns);
      else if (format === 'xlsx') exportXLSX(data, filename, receivableExportColumns);
      else if (format === 'pdf') exportPDF(data, filename, receivableExportColumns, 'Credit / Receivable Records', receivableReportSubtitle);
    } else {
      const filename = `purchases-${new Date().toISOString().split('T')[0]}`;
      const data = filteredPurchases;
      if (format === 'csv') exportCSV(data, filename, purchaseExportColumns);
      else if (format === 'xlsx') exportXLSX(data, filename, purchaseExportColumns);
      else if (format === 'pdf') exportPDF(data, filename, purchaseExportColumns, 'Purchase Records', purchaseReportSubtitle);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => window.history.back()} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Accounts & Purchases</h1>
            </div>
            <button
              onClick={() => {
                if (activeTab === 'receivables') {
                  if (showCreditForm) {
                    resetCreditForm();
                    setShowCreditForm(false);
                  } else {
                    resetCreditForm();
                    setShowCreditForm(true);
                  }
                } else {
                  if (showForm) {
                    setShowForm(false);
                    setEditingPurchase(null);
                    setVendorName('');
                    setVendorContact('');
                    setPurchaseDate(new Date().toISOString().split('T')[0]);
                    setParticulars([{ description: '', category: '', subCategory: '', quantity: 1, rate: 0, amount: 0 }]);
                  } else {
                    setEditingPurchase(null);
                    setVendorName('');
                    setVendorContact('');
                    setPurchaseDate(new Date().toISOString().split('T')[0]);
                    setParticulars([{ description: '', category: '', subCategory: '', quantity: 1, rate: 0, amount: 0 }]);
                    setShowForm(true);
                  }
                }
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              {activeTab === 'receivables'
                ? (showCreditForm ? 'Close' : 'New Credit')
                : (showForm ? 'Close' : 'New Purchase')}
            </button>
          </div>
          <div className="flex space-x-1 mt-4">
            <button
              onClick={() => setActiveTab('purchases')}
              className={`px-4 py-2 rounded-t-lg font-medium text-sm ${activeTab === 'purchases' ? 'bg-gray-100 text-gray-900 border border-b-0 border-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Purchases (Payable)
            </button>
            <button
              onClick={() => setActiveTab('receivables')}
              className={`px-4 py-2 rounded-t-lg font-medium text-sm ${activeTab === 'receivables' ? 'bg-gray-100 text-gray-900 border border-b-0 border-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Credits (Receivable)
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowForm(false); setEditingPurchase(null); }}>
            <div className="bg-white rounded-lg p-4 w-full max-w-4xl mx-4 shadow-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">{editingPurchase ? 'Edit Purchase' : 'New Purchase'}</h2>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingPurchase(null); }}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
              <form onSubmit={handlePurchaseSubmit}>
                <div className="border-2 border-gray-800 bg-white p-6 md:p-8">
              <div className="border-b-2 border-gray-800 pb-4 mb-6">
                <h2 className="text-3xl font-bold text-center tracking-widest text-gray-900">PURCHASE BILL</h2>
                <p className="text-center text-sm text-gray-600 mt-1">Hospital Management System</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Vendor / Purchased From</p>
                  <input type="text" list="vendor-list" value={vendorName} onChange={handleVendorNameChange} placeholder="Vendor Name" className="form-input w-full font-semibold" required />
                  <input type="text" value={vendorContact} onChange={(e) => setVendorContact(e.target.value)} placeholder="Contact" className="form-input w-full mt-2" />
                </div>
                <div className="md:text-right">
                  <div className="mb-2">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Bill Date</p>
                    <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="form-input w-full md:w-48 md:ml-auto" required />
                  </div>
                  <p className="text-xs text-gray-500">Bill No: <span className="font-mono text-gray-800">AUTO-GENERATED</span></p>
                </div>
              </div>

              <h3 className="text-sm font-bold uppercase tracking-wider mb-2 border-b border-gray-300 pb-1">Particulars</h3>
              <table className="w-full border-collapse border border-gray-800 mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-800 px-2 py-1 text-left text-xs w-12">S.No</th>
                    <th className="border border-gray-800 px-2 py-1 text-left text-xs">Category</th>
                    <th className="border border-gray-800 px-2 py-1 text-left text-xs">Sub Category</th>
                    <th className="border border-gray-800 px-2 py-1 text-left text-xs">Description</th>
                    <th className="border border-gray-800 px-2 py-1 text-right text-xs w-16">Qty</th>
                    <th className="border border-gray-800 px-2 py-1 text-right text-xs w-24">Rate</th>
                    <th className="border border-gray-800 px-2 py-1 text-right text-xs w-24">Amount</th>
                    <th className="border border-gray-800 px-2 py-1 text-center text-xs w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {particulars.map((p, idx) => (
                    <tr key={idx}>
                      <td className="border border-gray-800 px-2 py-1 text-xs text-center">{idx + 1}</td>
                      <td className="border border-gray-800 px-1 py-1">
                        <input type="text" list="category-list" value={p.category} onChange={(e) => handleParticularChange(idx, 'category', e.target.value)} className="w-full border-0 p-1 text-sm focus:ring-0 outline-none" placeholder="Category" required />
                      </td>
                      <td className="border border-gray-800 px-1 py-1">
                        <input type="text" list="subcategory-list" value={p.subCategory} onChange={(e) => handleParticularChange(idx, 'subCategory', e.target.value)} className="w-full border-0 p-1 text-sm focus:ring-0 outline-none" placeholder="Sub Category" />
                      </td>
                      <td className="border border-gray-800 px-1 py-1">
                        <input type="text" value={p.description} onChange={(e) => handleParticularChange(idx, 'description', e.target.value)} className="w-full border-0 p-1 text-sm focus:ring-0 outline-none" placeholder="Description" required />
                      </td>
                      <td className="border border-gray-800 px-1 py-1">
                        <input type="number" min="0" value={p.quantity} onChange={(e) => handleParticularChange(idx, 'quantity', e.target.value)} className="w-full border-0 p-1 text-sm text-right focus:ring-0 outline-none" required />
                      </td>
                      <td className="border border-gray-800 px-1 py-1">
                        <input type="number" min="0" step="0.01" value={p.rate} onChange={(e) => handleParticularChange(idx, 'rate', e.target.value)} className="w-full border-0 p-1 text-sm text-right focus:ring-0 outline-none" required />
                      </td>
                      <td className="border border-gray-800 px-2 py-1 text-right text-sm font-semibold">₹{p.amount.toFixed(2)}</td>
                      <td className="border border-gray-800 px-2 py-1 text-center">
                        {particulars.length > 1 && (
                          <button type="button" onClick={() => removeParticular(idx)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <datalist id="category-list">
                {categories.map((c, i) => <option key={i} value={c} />)}
              </datalist>
              <datalist id="subcategory-list">
                {subCategories.map((c, i) => <option key={i} value={c} />)}
              </datalist>
              <datalist id="vendor-list">
                {vendorNames.map((v, i) => <option key={i} value={v} />)}
              </datalist>

              <div className="flex justify-between items-start md:items-center mb-6">
                <button type="button" onClick={addParticular} className="text-blue-600 text-sm flex items-center hover:underline">
                  <Plus className="h-4 w-4 mr-1" /> Add Particular
                </button>
                <div className="text-right mt-2 md:mt-0">
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-2xl font-bold text-gray-900">₹{totalAmount.toFixed(2)}</p>
                </div>
              </div>

              <div className="border-t border-gray-300 pt-4 flex flex-col md:flex-row justify-between items-end gap-4">
                <div>
                  <p className="text-xs text-gray-600">Authorized Signature</p>
                  <p className="mt-4 border-b border-gray-800 w-40"></p>
                </div>
                <div className="flex items-center space-x-2">
                  <button type="button" onClick={() => { setShowForm(false); setEditingPurchase(null); }} disabled={submittingPurchase} className="btn-secondary px-4 py-2 rounded disabled:opacity-50">Cancel</button>
                  <button type="submit" disabled={submittingPurchase} className="bg-green-600 text-white px-8 py-2 rounded hover:bg-green-700 font-semibold disabled:opacity-60 flex items-center">
                    {submittingPurchase ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {editingPurchase ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingPurchase ? 'Update Purchase' : 'Create Purchase'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
            </div>
          </div>
        )}

        {activeTab === 'purchases' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Purchase Analysis</h2>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Vendor Name</p>
                <input
                  type="text"
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  placeholder="Filter by vendor..."
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Month</p>
                <input
                  type="month"
                  value={monthFilter}
                  onChange={(e) => {
                    setMonthFilter(e.target.value);
                    setFromDate('');
                    setToDate('');
                  }}
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">From Date</p>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setMonthFilter('');
                  }}
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">To Date</p>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setMonthFilter('');
                  }}
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Date Order</p>
                <select
                  value={purchaseDateSort}
                  onChange={(e) => setPurchaseDateSort(e.target.value)}
                  className="form-input w-full"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={resetFilters} className="btn-secondary px-4 py-2 rounded">
                  Reset
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 bg-gray-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Bills</p>
                <p className="text-2xl font-bold text-gray-900">{analysis.count}</p>
              </div>
              <div className="flex-1 bg-yellow-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-yellow-800 uppercase tracking-wider">Total</p>
                <p className="text-2xl font-bold text-yellow-800">₹{analysis.total.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-green-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-green-800 uppercase tracking-wider">Paid</p>
                <p className="text-2xl font-bold text-green-800">₹{analysis.paid.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-red-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Unpaid</p>
                <p className="text-2xl font-bold text-red-700">₹{analysis.unpaid.toFixed(2)}</p>
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Purchase Records</h2>
              <div className="flex items-center space-x-2">
                <select
                  onChange={(e) => {
                    const format = e.target.value;
                    if (format) handleExport(format);
                    e.target.value = '';
                  }}
                  className="form-input text-sm"
                  defaultValue=""
                >
                  <option value="" disabled>Export</option>
                  <option value="csv">CSV</option>
                  <option value="xlsx">XLSX</option>
                  <option value="pdf">PDF</option>
                </select>
                <button
                  onClick={() => printData(filteredPurchases, purchaseExportColumns, 'Purchase Records', purchaseReportSubtitle)}
                  className="bg-gray-600 text-white p-2 rounded hover:bg-gray-700"
                  title="Print"
                >
                  <Printer className="h-4 w-4" />
                </button>
              </div>
            </div>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Loading...</p>
            ) : filteredPurchases.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No purchases found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Bill No</th>
                      <th className="text-left py-3 px-4">Vendor</th>
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-left py-3 px-4">Total</th>
                      <th className="text-left py-3 px-4">Paid</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPurchases.map((purchase) => (
                      <tr key={purchase._id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono">{purchase.billNumber}</td>
                        <td className="py-3 px-4">
                          <p className="font-medium">{purchase.vendorName}</p>
                          <p className="text-sm text-gray-500">{purchase.vendorContact}</p>
                        </td>
                        <td className="py-3 px-4">{new Date(purchase.purchaseDate).toLocaleDateString('en-GB')}</td>
                        <td className="py-3 px-4">₹{purchase.totalAmount.toFixed(2)}</td>
                        <td className="py-3 px-4">₹{purchase.totalPaid.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusClass(purchase.status)}`}>
                            {purchase.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-2">
                            {purchase.status !== 'paid' && (
                              <button
                                onClick={() => openPayment(purchase)}
                                className="bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
                                title="Pay"
                              >
                                <CreditCard className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => downloadVoucher(purchase._id, purchase.billNumber)}
                              className="bg-green-600 text-white p-2 rounded hover:bg-green-700"
                              title="Download Voucher"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            {canManagePurchases && purchase.status !== 'paid' && (
                              <button
                                onClick={() => handleEditPurchase(purchase)}
                                className="bg-gray-600 text-white p-2 rounded hover:bg-gray-700"
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                            {canManagePurchases && (
                              <button
                                onClick={() => handleDeletePurchase(purchase)}
                                className="bg-red-600 text-white p-2 rounded hover:bg-red-700"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        )}

        {activeTab === 'receivables' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Credit Analysis</h2>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Party Name</p>
                <input
                  type="text"
                  value={partyFilter}
                  onChange={(e) => setPartyFilter(e.target.value)}
                  placeholder="Filter by party..."
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Category</p>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="form-input w-full"
                >
                  <option value="">All Categories</option>
                  {allCreditCategories.map((c, i) => <option key={i} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Month</p>
                <input
                  type="month"
                  value={rMonthFilter}
                  onChange={(e) => {
                    setRMonthFilter(e.target.value);
                    setRFromDate('');
                    setRToDate('');
                  }}
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">From Date</p>
                <input
                  type="date"
                  value={rFromDate}
                  onChange={(e) => {
                    setRFromDate(e.target.value);
                    setRMonthFilter('');
                  }}
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">To Date</p>
                <input
                  type="date"
                  value={rToDate}
                  onChange={(e) => {
                    setRToDate(e.target.value);
                    setRMonthFilter('');
                  }}
                  className="form-input w-full"
                />
              </div>
              <div className="flex-1" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Date Order</p>
                <select
                  value={receivableDateSort}
                  onChange={(e) => setReceivableDateSort(e.target.value)}
                  className="form-input w-full"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={resetReceivableFilters} className="btn-secondary px-4 py-2 rounded">
                  Reset
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 bg-gray-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Credits</p>
                <p className="text-2xl font-bold text-gray-900">{receivableAnalysis.count}</p>
              </div>
              <div className="flex-1 bg-yellow-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-yellow-800 uppercase tracking-wider">Total</p>
                <p className="text-2xl font-bold text-yellow-800">₹{receivableAnalysis.total.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-green-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-green-800 uppercase tracking-wider">Received</p>
                <p className="text-2xl font-bold text-green-800">₹{receivableAnalysis.received.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-red-100 p-4 rounded-lg" style={{ minWidth: '12rem' }}>
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">To Collect</p>
                <p className="text-2xl font-bold text-red-700">₹{receivableAnalysis.pending.toFixed(2)}</p>
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Credit / Receivable Records</h2>
              <div className="flex items-center space-x-2">
                <select
                  onChange={(e) => {
                    const format = e.target.value;
                    if (format) handleExport(format);
                    e.target.value = '';
                  }}
                  className="form-input text-sm"
                  defaultValue=""
                >
                  <option value="" disabled>Export</option>
                  <option value="csv">CSV</option>
                  <option value="xlsx">XLSX</option>
                  <option value="pdf">PDF</option>
                </select>
                <button
                  onClick={() => printData(filteredReceivables, receivableExportColumns, 'Credit / Receivable Records', receivableReportSubtitle)}
                  className="bg-gray-600 text-white p-2 rounded hover:bg-gray-700"
                  title="Print"
                >
                  <Printer className="h-4 w-4" />
                </button>
              </div>
            </div>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Loading...</p>
            ) : filteredReceivables.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No credits found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Credit No</th>
                      <th className="text-left py-3 px-4">Party</th>
                      <th className="text-left py-3 px-4">Category</th>
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-left py-3 px-4">Total</th>
                      <th className="text-left py-3 px-4">Received</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReceivables.map((receivable) => (
                      <tr key={receivable._id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono">{receivable.creditNumber}</td>
                        <td className="py-3 px-4">
                          <p className="font-medium">{receivable.partyName}</p>
                          <p className="text-sm text-gray-500">{receivable.partyContact}</p>
                          {receivable.description && <p className="text-xs text-gray-400 mt-1">{receivable.description}</p>}
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                            {receivable.category}
                          </span>
                        </td>
                        <td className="py-3 px-4">{new Date(receivable.creditDate).toLocaleDateString('en-GB')}</td>
                        <td className="py-3 px-4">₹{receivable.totalAmount.toFixed(2)}</td>
                        <td className="py-3 px-4">₹{receivable.totalReceived.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getReceivableStatusClass(receivable.status)}`}>
                            {receivable.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-2">
                            {receivable.status !== 'received' && (
                              <button
                                onClick={() => openReceipt(receivable)}
                                className="bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
                                title="Record Receipt"
                              >
                                <CreditCard className="h-4 w-4" />
                              </button>
                            )}
                            {canManagePurchases && receivable.status !== 'received' && (
                              <button
                                onClick={() => handleEditReceivable(receivable)}
                                className="bg-gray-600 text-white p-2 rounded hover:bg-gray-700"
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                            {canManagePurchases && (
                              <button
                                onClick={() => handleDeleteReceivable(receivable)}
                                className="bg-red-600 text-white p-2 rounded hover:bg-red-700"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {paymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
            <p className="text-sm text-gray-600 mb-2">Vendor: {paymentModal.vendorName}</p>
            <p className="text-sm text-gray-600 mb-4">Balance: ₹{paymentModal.balanceAmount.toFixed(2)}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input type="number" min="0" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} className="form-input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="form-input w-full">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="online">Online Transfer</option>
                </select>
              </div>
              {paymentMethod !== 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference/UTR</label>
                  <input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} className="form-input w-full" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setPaymentModal(null)} disabled={submittingPayment} className="px-4 py-2 bg-gray-200 rounded text-sm disabled:opacity-50">Cancel</button>
              <button onClick={submitPayment} disabled={submittingPayment} className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-60 flex items-center">
                {submittingPayment ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving...
                  </>
                ) : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowCreditForm(false); resetCreditForm(); }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 shadow-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingReceivable ? 'Edit Credit' : 'New Credit (Receivable)'}</h2>
              <button
                type="button"
                onClick={() => { setShowCreditForm(false); resetCreditForm(); }}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleCreditSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Party Name *</label>
                  <input type="text" list="party-list" value={partyName} onChange={handlePartyNameChange} placeholder="e.g. ABC Pharmacy, Insurance Co." className="form-input w-full" required />
                  <datalist id="party-list">
                    {receivableParties.map((p, i) => <option key={i} value={p} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                  <input type="text" value={partyContact} onChange={(e) => setPartyContact(e.target.value)} placeholder="Phone / Email" className="form-input w-full" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <input type="text" list="credit-category-list" value={creditCategory} onChange={(e) => setCreditCategory(e.target.value)} placeholder="e.g. Pharmacy, Insurance" className="form-input w-full" required />
                    <datalist id="credit-category-list">
                      {allCreditCategories.map((c, i) => <option key={i} value={c} />)}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input type="date" value={creditDate} onChange={(e) => setCreditDate(e.target.value)} className="form-input w-full" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount to Collect *</label>
                  <input type="number" min="0" step="0.01" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="0.00" className="form-input w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={creditDescription} onChange={(e) => setCreditDescription(e.target.value)} placeholder="Details about this credit" rows="2" className="form-input w-full" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => { setShowCreditForm(false); resetCreditForm(); }} disabled={submittingCredit} className="btn-secondary px-4 py-2 rounded disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={submittingCredit} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 font-semibold disabled:opacity-60 flex items-center">
                  {submittingCredit ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {editingReceivable ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingReceivable ? 'Update Credit' : 'Create Credit'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {receiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Record Receipt</h3>
            <p className="text-sm text-gray-600 mb-2">Party: {receiptModal.partyName}</p>
            <p className="text-sm text-gray-600 mb-4">Balance: ₹{receiptModal.balanceAmount.toFixed(2)}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input type="number" min="0" step="0.01" value={receiptAmount} onChange={(e) => setReceiptAmount(e.target.value)} className="form-input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={receiptMethod} onChange={(e) => setReceiptMethod(e.target.value)} className="form-input w-full">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="online">Online Transfer</option>
                </select>
              </div>
              {receiptMethod !== 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference/UTR</label>
                  <input type="text" value={receiptRef} onChange={(e) => setReceiptRef(e.target.value)} className="form-input w-full" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setReceiptModal(null)} disabled={submittingReceipt} className="px-4 py-2 bg-gray-200 rounded text-sm disabled:opacity-50">Cancel</button>
              <button onClick={submitReceipt} disabled={submittingReceipt} className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-60 flex items-center">
                {submittingReceipt ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving...
                  </>
                ) : 'Save Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsPage;
